// Canvasses API functions
import { config } from '../config/environment';
import { logger } from './logger';

// Prefer environment variable for the Canvass Service
// Example: REACT_APP_CANVASS_API_BASE=https://<your-canvass-service>.azurewebsites.net/api
const CANVASS_API_BASE =
  config.canvassApiBase ||
  ''; // Leave empty to force passing apiBase explicitly

/**
 * Get Azure AD access token for Canvass API authentication
 * Uses the existing MSAL instance with canvass-specific scopes
 * @returns {Promise<string>} The access token
 */
const getAccessToken = async () => {
  // First try to use the global token getter set up by useAuthenticatedApi hook
  if (typeof window !== 'undefined' && window.getAccessToken) {
    try {
      // Determine the correct scope based on environment
      const scope = config.isProduction 
        ? `api://${process.env.REACT_APP_AZURE_AD_CLIENT_ID}/canvasses-production.access_as_user`
        : `api://${process.env.REACT_APP_AZURE_AD_CLIENT_ID}/canvasses-dev.access_as_user`;
      
      return await window.getAccessToken(scope);
    } catch (error) {
      logger.warn('Global getAccessToken failed, falling back to direct MSAL:', error);
    }
  }
  
  // Fallback to direct MSAL access for non-React contexts
  const { msalInstance } = await import('../config/msalConfig');
  
  // Determine the correct scope based on environment
  const environment = process.env.NODE_ENV || 'development';
  const scope = environment === 'production' 
    ? `api://${process.env.REACT_APP_AZURE_AD_CLIENT_ID}/canvasses-production.access_as_user`
    : `api://${process.env.REACT_APP_AZURE_AD_CLIENT_ID}/canvasses-dev.access_as_user`;
  
  const accounts = msalInstance.getAllAccounts();
  
  if (accounts.length === 0) {
    throw new Error('No authenticated user found. Please log in first.');
  }
  
  const account = accounts[0];
  
  try {
    // Try to get token silently first with canvass-specific scope
    const response = await msalInstance.acquireTokenSilent({
      scopes: [scope],
      account: account,
    });
    return response.accessToken;
  } catch (error) {
    // If silent acquisition fails, try interactive method
    if (error.errorCode === 'consent_required' || 
        error.errorCode === 'interaction_required' || 
        error.errorCode === 'login_required') {
      try {
        const response = await msalInstance.acquireTokenPopup({
          scopes: [scope],
        });
        return response.accessToken;
      } catch (interactiveError) {
        throw new Error(`Failed to acquire access token: ${interactiveError.message}`);
      }
    }
    throw new Error(`Failed to acquire access token: ${error.message}`);
  }
};

/**
 * Fetch a single canvass by canvassId.
 * Backend route registered in Canvass Service:
 *  GET /canvasses/getCanvassById?canvassId=<id>
 * We call it via the function URL base as: `${CANVASS_API_BASE}/getCanvassById?...`
 *
 * @param {Object} options
 * @param {string} options.canvassId - The canvassId to retrieve
 * @param {boolean} [options.returnFull=false] - When true, returns the full API payload
 * @param {string} [options.apiBase] - Optional base URL override
 * @returns {Promise<Object>} By default returns the canvass object (or null if not found)
 */
export const fetchCanvassById = async (options = {}) => {
  const { canvassId, returnFull = false, apiBase } = options;

  if (!canvassId) throw new Error('fetchCanvassById: canvassId is required');

  const base = apiBase || CANVASS_API_BASE;
  if (!base) {
    throw new Error(
      'Canvass API base URL not set. Provide REACT_APP_CANVASS_API_BASE or pass options.apiBase.'
    );
  }

  const params = new URLSearchParams();
  params.append('canvassId', String(canvassId));

  // Function route name is 'get-canvasses-by-id' with route 'canvasses/getCanvassById'
  // Follow the existing pattern using the function base without the 'canvasses/' prefix
  const apiUrl = `${base}/getCanvassById?${params}`;

  try {
    const token = await getAccessToken();
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });
    const result = await response.json();

    if (!response.ok || result.success === false) {
      logger.error('Error fetching canvass by id:', result);
      throw new Error(result.error || result.message || 'Failed to fetch canvass');
    }

    if (returnFull) return result;
    return result.canvass ?? null;
  } catch (error) {
    logger.error('Exception fetching canvass by id:', error?.message || error);
    throw error;
  }
};

/**
 * Fetch the status of a canvass by its ID.
 * Uses the efficient status endpoint that returns only canvassId and status.
 * @param {Object} options
 * @param {string} options.canvassId - The canvassId to retrieve status for
 * @param {string} [options.apiBase] - Optional base URL override
 * @returns {Promise<Object>} Object with canvassId and status
 */
export const fetchCanvassStatus = async (options = {}) => {
  const { canvassId, apiBase } = options;

  if (!canvassId) throw new Error('fetchCanvassStatus: canvassId is required');

  const base = apiBase || CANVASS_API_BASE;
  if (!base) {
    throw new Error(
      'Canvass API base URL not set. Provide REACT_APP_CANVASS_API_BASE or pass options.apiBase.'
    );
  }

  // Use the status endpoint: canvasses/status/{canvassId}
  const apiUrl = `${base}/status/${encodeURIComponent(canvassId)}`;

  try {
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    const result = await response.json();

    if (!response.ok || result.success === false) {
      logger.error('Error fetching canvass status:', result);
      throw new Error(result.error || result.message || 'Failed to fetch canvass status');
    }

    return result.canvass || null;
  } catch (error) {
    logger.error('Exception fetching canvass status:', error?.message || error);
    throw error;
  }
};

/**
 * Fetch paginated canvasses.
 * @param {Object} options
 * @param {number} options.page - Page number (default 1)
 * @param {number} options.pageSize - Page size (default 20)
 * @param {boolean} options.returnFull - When true, returns full API payload including pagination (default false)
 * @param {string} options.apiBase - Optional base URL override, e.g. https://.../api
 * @returns {Promise<Array|Object>}
 */
export const fetchCanvasses = async (options = {}) => {
  const {
    page = 1,
    pageSize = 20,
    selectedStatus,
    companyId,
    returnFull = false,
    apiBase
  } = options;

  const base = apiBase || CANVASS_API_BASE;
  if (!base) {
    throw new Error(
      'Canvass API base URL not set. Provide REACT_APP_CANVASS_API_BASE or pass options.apiBase.'
    );
  }

  const params = new URLSearchParams();
  params.append('page', String(page));
  params.append('pageSize', String(pageSize));
  // Remove ownerId - backend will determine from authenticated user
  if (selectedStatus) params.append('status', selectedStatus || null);
  if (companyId) params.append('companyId', companyId);
  const apiUrl = `${base}/getAllCanvasses?${params}`;

  try {
    // Add authentication headers
    const token = await getAccessToken(); // Your Azure AD token function
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    const result = await response.json();

    if (!response.ok) {
      logger.error('Error fetching canvasses:', result);
      throw new Error(result.error || 'Failed to fetch canvasses');
    }

    // By default, return just the canvasses array (similar to fetchChatHistory)
    if (!returnFull) {
      return result.canvasses || [];
    }

    // Return full payload with pagination metadata
    return {
      canvasses: result.canvasses || [],
      page: result.page ?? page,
      pageSize: result.pageSize ?? pageSize,
      totalCount: result.totalCount ?? null,
      totalPages: result.totalPages ?? null,
      message: result.message || '',
      success: result.success === true,
    };
  } catch (error) {
    logger.error('Exception fetching canvasses:', error?.message || error);
    throw error;
  }
};

/**
 * Search canvasses by canvassId (partial match), with pagination.
 * @param {Object} options
 * @param {string} options.search - Search term (matches canvassId via CONTAINS)
 * @param {number} options.page - Page number (default 1)
 * @param {number} options.pageSize - Page size (default 20)
 * @param {boolean} options.returnFull - When true, returns full API payload including pagination (default false)
 * @param {string} options.apiBase - Optional base URL override, e.g. https://.../api
 * @returns {Promise<Array|Object>}
 */
export const searchCanvasses = async (options = {}) => {
  const {
    search = '',
    page = 1,
    pageSize = 20,
    ownerId,
    selectedStatus,
    returnFull = false,
    apiBase, // optional override
  } = options;

  const base = apiBase || CANVASS_API_BASE;
  if (!base) {
    throw new Error(
      'Canvass API base URL not set. Provide REACT_APP_CANVASS_API_BASE or pass options.apiBase.'
    );
  }

  const params = new URLSearchParams();
  if (search) params.append('search', String(search));
  params.append('page', String(page));
  params.append('pageSize', String(pageSize));
  // Remove ownerId and isAdmin - backend will determine from authenticated user
  if (selectedStatus) params.append('status', selectedStatus || null);

  const apiUrl = `${base}/search?${params}`;

  try {
    const token = await getAccessToken();
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    const result = await response.json();

    if (!response.ok) {
      logger.error('Error searching canvasses:', result);
      throw new Error(result.error || 'Failed to search canvasses');
    }

    if (!returnFull) {
      return result.canvasses || [];
    }

    return {
      canvasses: result.canvasses || [],
      page: result.page ?? page,
      pageSize: result.pageSize ?? pageSize,
      totalCount: result.totalCount ?? null,
      totalPages: result.totalPages ?? null,
      message: result.message || '',
      success: result.success === true,
    };
  } catch (error) {
    logger.error('Exception searching canvasses:', error?.message || error);
    throw error;
  }
};

/**
 * Fetch the (first) interview message thread for a given canvassId.
 * Backend route is defined in Canvass Service:
 *  GET /canvasses/get-client-interview?canvassId=<id>
 *
 * @param {Object} options
 * @param {string} options.canvassId - The canvass/conversation/thread id
 * @param {boolean} [options.returnFull=false] - When true, return the full API payload
 * @param {string} [options.apiBase] - Optional base URL override
 * @returns {Promise<Array|Object>} Default: interview messages array (may be []), or full payload when returnFull
 */
export const fetchCanvassOnboardingInterview = async (options = {}) => {
  const {
    canvassId,
    returnFull = false,
    apiBase,
  } = options;

  if (!canvassId) {
    throw new Error('fetchCanvassClientInterview: canvassId is required');
  }

  const base = apiBase || CANVASS_API_BASE;
  if (!base) {
    throw new Error(
      'Canvass API base URL not set. Provide REACT_APP_CANVASS_API_BASE or pass options.apiBase.'
    );
  }

  const params = new URLSearchParams();
  params.append('canvassId', String(canvassId));

  // Backend registers route name 'get-canvass-client-interview' with route 'canvasses/get-client-interview'
  const apiUrl = `${base}/get-client-interview?${params}`;

  try {
    const token = await getAccessToken();
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    const result = await response.json();

    if (!response.ok || result.success === false) {
      logger.error('Error fetching canvass client interview:', result);
      throw new Error(result.error || result.message || 'Failed to fetch canvass client interview');
    }

    // Backend returns { success, interviewMessage, message }
    // but be defensive and support interviewMessages as well
    const messages = result.interviewMessage || result.interviewMessages || [];

    if (returnFull) return result;
    return messages;
  } catch (error) {
    logger.error('Exception fetching canvass client interview:', error?.message || error);
    throw error;
  }
}

/**
 * Save a canvass document.
 * Backend route registered in Canvass Service:
 *  POST /canvasses/save
 * We call it via the function URL base as: `${CANVASS_API_BASE}/save`
 *
 * Minimal expected payload (see backend CanvassDocument for full shape):
 * {
 *   canvassId: string,
 *   interviewLink: string,
 *   audience: string,
 *   environment: string,
 *   interview_introductions: Array,
 *   main_objective: Object,
 *   sub_objectives: Array,
 *   touchpoints: Array,
 *   constraints: Object,
 *   success_metrics: Array
 *   // id is optional; server will set id = canvassId if missing
 * }
 *
 * @param {Object} doc - The canvass document to save
 * @param {Object} [options]
 * @param {boolean} [options.returnFull=false] - When true, returns the full API payload
 * @param {string} [options.apiBase] - Optional base URL override
 * @returns {Promise<Object>} By default returns the saved canvass object
 */
export const saveCanvass = async (doc, options = {}) => {
  const { returnFull = false, apiBase } = options;

  if (!doc || typeof doc !== 'object') {
    throw new Error('saveCanvass: doc is required and must be an object');
  }

  const base = apiBase || CANVASS_API_BASE;
  if (!base) {
    throw new Error(
      'Canvass API base URL not set. Provide REACT_APP_CANVASS_API_BASE or pass options.apiBase.'
    );
  }

  // Backend registers route name 'post-update-canvass' with route 'canvasses/update'
  const apiUrl = `${base}/update`;

  try {
    const token = await getAccessToken();
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(doc),
    });

    const result = await response.json();

    if (!response.ok || result.success === false) {
      logger.error('Error saving canvass:', result);
      throw new Error(result.error || result.message || 'Failed to save canvass');
    }

    if (returnFull) return result;
    return result.canvass;
  } catch (error) {
    logger.error('Exception saving canvass:', error?.message || error);
    throw error;
  }
};

/**
 * Fetch a canvass report.
 * Backend route is defined in Canvass Service:
 *  GET /get-canvass-report/{canvassId}
 *
 * @param {Object|string} doc - The canvassId as a string or an object with canvassId
 * @param {Object} [options]
 * @param {boolean} [options.returnFull=false] - When true, returns the full API payload
 * @param {string} [options.apiBase] - Optional base URL override
 * @returns {Promise<Object>} By default returns the canvass report object
 */
export const getCanvassReport = async (doc, options = {}) => {
  const { returnFull = false, apiBase } = options;

  const canvassId = typeof doc === 'string' ? doc : doc?.canvassId;
  if (!canvassId) {
    throw new Error('getCanvassReport: canvassId is required');
  }

  const base = apiBase || CANVASS_API_BASE;
  if (!base) {
    throw new Error(
      'Canvass API base URL not set. Provide REACT_APP_CANVASS_API_BASE or pass options.apiBase.'
    );
  }

  const queryUrl = `${base}/getCanvassReport/${encodeURIComponent(canvassId)}?canvassId=${encodeURIComponent(canvassId)}`;
  const pathUrl = `${base}/getCanvassReport/${encodeURIComponent(canvassId)}`;

  try {
    const token = await getAccessToken();
    // Prefer querystring form because backend handler reads searchParams
    let response = await fetch(pathUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    // Fallback to path-param route if query route is not available
    if (!response.ok && response.status === 404) {
      response = await fetch(queryUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
    }

    const result = await response.json();

    if (!response.ok || result.success === false) {
      logger.error('Error fetching canvass report:', result);
      throw new Error(result.error || result.message || 'Failed to fetch canvass report');
    }

    if (returnFull) return result;
    return result.report ?? null;
  } catch (error) {
    logger.error('Exception fetching canvass report:', error?.message || error);
    throw error;
  }
}

/**
 * Activate a canvass by calling the backend POST endpoint.
 * Backend routes in Canvass Service:
 *  POST /canvasses/activate/{canvassId}
 *
 * @param {string} canvassId - The canvass id to activate
 * @param {Object} [options]
 * @param {string} [options.apiBase] - Optional base URL override
 * @returns {Promise<Object>} Returns an object with activateResult
 */
export const activateCanvass = async (canvassId, options = {}) => {
  const { apiBase } = options;
  if (!canvassId) throw new Error('activateCanvass: canvassId is required');

  const base = apiBase || CANVASS_API_BASE;
  if (!base) {
    throw new Error(
      'Canvass API base URL not set. Provide REACT_APP_CANVASS_API_BASE or pass options.apiBase.'
    );
  }

  // 1) Activate
  const activateUrl = `${base}/activate/${encodeURIComponent(canvassId)}`;
  const token = await getAccessToken();
  const activateResp = await fetch(activateUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });
  const activateResult = await activateResp.json();
  if (!activateResp.ok || activateResult.success === false) {
    logger.error('Error activating canvass:', activateResult);
    throw new Error(activateResult.error || activateResult.message || 'Failed to activate canvass');
  }

  return { activateResult };
};

/**
 * Complete a canvass by calling the backend POST endpoint.
 * Backend routes in Canvass Service:
 *  POST /canvasses/complete/{canvassId}
 *
 * @param {string} canvassId - The canvass id to activate
 * @param {Object} [options]
 * @param {string} [options.apiBase] - Optional base URL override
 * @returns {Promise<Object>} Returns an object with activateResult
 */
export const completeCanvass = async (canvassId, options = {}) => {
  const { apiBase } = options;
  if (!canvassId) throw new Error('completeCanvass: canvassId is required');

  const base = apiBase || CANVASS_API_BASE;
  if (!base) {
    throw new Error(
      'Canvass API base URL not set. Provide REACT_APP_CANVASS_API_BASE or pass options.apiBase.'
    );
  }
  
  const completeUrl = `${base}/complete/${encodeURIComponent(canvassId)}`;
  const token = await getAccessToken();
  const completeResp = await fetch(completeUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });
  const completeResult = await completeResp.json();
  if (!completeResp.ok || completeResult.success === false) {
    logger.error('Error completing canvass:', completeResult);
    throw new Error(completeResult.error || completeResult.message || 'Failed to complete canvass');
  }

  return { completeResult };
};

/**
 * Duplicate a canvass by calling the backend POST endpoint.
 * Backend routes in Canvass Service:
 *  POST /canvasses/duplicate/{canvassId}
 *
 * @param {string} canvassId - The canvass id to duplicate
 * @param {Object} [options]
 * @param {boolean} [options.returnFull=false] - When true, returns the full API payload
 * @param {string} [options.apiBase] - Optional base URL override
 * @returns {Promise<Object>} Returns the duplicated canvass object
 */
export const duplicateCanvass = async (canvassId, options = {}) => {
  const { returnFull = false, apiBase } = options;
  if (!canvassId) throw new Error('duplicateCanvass: canvassId is required');

  const base = apiBase || CANVASS_API_BASE;
  if (!base) {
    throw new Error(
      'Canvass API base URL not set. Provide REACT_APP_CANVASS_API_BASE or pass options.apiBase.'
    );
  }

  const duplicateUrl = `${base}/duplicate/${encodeURIComponent(canvassId)}`;

  try {
    const token = await getAccessToken();
    const response = await fetch(duplicateUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });
    const result = await response.json();

    if (!response.ok || result.success === false) {
      logger.error('Error duplicating canvass:', result);
      throw new Error(result.error || result.message || 'Failed to duplicate canvass');
    }

    if (returnFull) return result;
    return result.canvass ?? null;
  } catch (error) {
    logger.error('Exception duplicating canvass:', error?.message || error);
    throw error;
  }
};

/**
 * Delete a canvass by calling the backend DELETE endpoint.
 * Backend routes in Canvass Service:
 *  DELETE /canvasses/delete/{canvassId}
 *
 * @param {string} canvassId - The canvass id to delete
 * @param {Object} [options]
 * @param {boolean} [options.returnFull=false] - When true, returns the full API payload
 * @param {string} [options.apiBase] - Optional base URL override
 * @returns {Promise<Object>} Returns the deleted canvass payload (if any)
 */
export const deleteCanvass = async (canvassId, options = {}) => {
  const { returnFull = false, apiBase } = options;
  if (!canvassId) throw new Error('deleteCanvass: canvassId is required');

  const base = apiBase || CANVASS_API_BASE;
  if (!base) {
    throw new Error(
      'Canvass API base URL not set. Provide REACT_APP_CANVASS_API_BASE or pass options.apiBase.'
    );
  }

  const deleteUrl = `${base}/delete/${encodeURIComponent(canvassId)}`;

  try {
    const token = await getAccessToken();
    const response = await fetch(deleteUrl, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });
    const result = await response.json();

    if (!response.ok || result.success === false) {
      logger.error('Error deleting canvass:', result);
      throw new Error(result.error || result.message || 'Failed to delete canvass');
    }

    if (returnFull) return result;
    return result.canvass ?? null;
  } catch (error) {
    logger.error('Exception deleting canvass:', error?.message || error);
    throw error;
  }
};


/**
 * Fetch user's personal canvasses (ownerId filtering)
 * @param {Object} options - Options object
 * @param {string} [options.companyId] - Optional company ID to filter canvasses
 * @param {string} [options.apiBase] - Optional base URL override
 * @returns {Promise<Object>} Object with success, canvasses array, and pagination info
 */
export const fetchUserCanvasses = async (options = {}) => {
  return fetchCanvasses(options);
};
